/**
 * Prompt Schema V2 — one compiler, five sections, for every agent type.
 *
 * Replaces three divergent builders:
 *   - src/lib/systemPromptBuilder.ts          (patient / customer / examiner)
 *   - CoachingSessionPage.buildCoachingPrompt (coaches)
 *   - TemplateCoachingSessionPage.buildCoachingPrompt (template coaches)
 *
 * Usage:
 *   const report = buildAgentPrompt(agent, { template, memorySummary });
 *   report.prompt  -> send as Anam systemPrompt
 *   report.tokens  -> live token feedback in the form
 */
import { buildPromptV2Detailed } from './adapters';
import type { AdapterContext, BuildResult } from './adapters';
import { compileWithReport } from './compile';
import type { CompileReport } from './compile';
import { assertNoLeak } from './audience';
import type { PromptSectionKey } from './types';

export * from './types';
export * from './compile';
export * from './audience';
export * from './parse';
export { buildPromptV2, buildPromptV2Detailed, intakeChecklist } from './adapters';
export type { AdapterContext, CoachingTemplateLike, BuildResult } from './adapters';

export interface BuildOptions extends AdapterContext {
  /**
   * Throw if learner/evaluator-only content reached the prompt.
   * On by default: a silent leak (exam answers, learner objectives) looks like
   * a working session while destroying its value, so failing loudly is safer.
   */
  enforceAudience?: boolean;
}

export interface AgentPromptReport extends CompileReport {
  /** Sections adopted verbatim from the author's own prompt. */
  authored: PromptSectionKey[];
  /** Sections generated from legacy fields because the author left them empty. */
  filled: PromptSectionKey[];
  /** Spoken identity actually used in the prompt. */
  personaName: string;
  /** Sections the author wrote more than once — a conversion error to review. */
  duplicates: PromptSectionKey[];
}

export function buildAgentPrompt(agent: Record<string, any>, options: BuildOptions = {}): AgentPromptReport {
  const { enforceAudience = true, ...ctx } = options;
  const built = buildPromptV2Detailed(agent, ctx);
  const report = compileWithReport(built.prompt);
  if (enforceAudience) assertNoLeak(report.prompt, agent);
  return {
    ...report,
    authored: built.authored,
    filled: built.filled,
    personaName: built.personaName,
    duplicates: built.duplicates,
  };
}
